// Vendored from github.com/Cynfu11/pdf-provenance js/valueLocator.js @ cbc7d91 (NFI-5884, HAR-2326). Change it there and re-copy; do not edit here.
/*
 * valueLocator.js -- find where an extracted value sits on its PDF page (page + box), from pdf.js text content.
 * Browser-side port of pdf_provenance/locate.py (same rules, same record). No dependencies: copy this file as-is.
 *
 *   const pages = [{ number, width, height, words: wordsFromItems(textContent.items, viewport) }, ...];
 *   const idx = buildIndex(pages);                       // once per document (optional: locate() builds one if given pages)
 *   const out = locate(idx, [{ key: 'Ending capital balance', value: '7,467,405.56', group: 'row-12' }], 'file.pdf');
 *   out[0].provenance -> { doc, page, bbox [x0, top, x1, bottom] (points, top-left origin, the displayed page), bbox_norm (0..1),
 *                          page_size, label_bbox, confidence (the locator's), extraction_path, match, rule, printed_text }
 * match: unique | label | hint | group | rounded | sign | percent | ambiguous (page/bbox null + candidates) | not_found.
 * ambiguous also carries pages (every page that prints the value, ascending) and group_page: the page the item's group (its
 * record) was placed on when the value is printed more than once there and nowhere else in the group (else null), with
 * group_page_count places on it. candidates lists the group page's places first.
 * rule: exact | date | text | rounded | sign | percent. rounded / sign / percent are fallbacks: used only when nothing prints the
 * value exactly, and only beside at least half of the key's words. value null -> provenance null.
 * A value is never placed where no rule can justify it.
 *
 * Loading: CommonJS (Node, webpack, jest) -> module.exports; a plain <script> -> window.valueLocator. Everything else stays inside
 * this function scope (a top-level name such as `top` would collide with browser globals like window.top).
 */

(function (root) {
const NUM_RE = /^(\d{1,3}(,\d{3})+(\.\d+)?|\d+(\.\d+)?|\.\d+)$/;
const EDGE_RE = /^[()[\]{},;:.!?"'`]+|[()[\]{},;:.!?"'`]+$/g;
const CURRENCY_CODES = ['USD', 'EUR', 'GBP'];
// minus signs statements print besides '-': U+2212, U+2012, U+2013, U+2014, U+FE63, U+FF0D
const MINUS = '-\u2212\u2012\u2013\u2014\ufe63\uff0d';
const FOOTNOTE_RE = /[*\u2020\u2021\u00a7]+$/;
const LINE_TOL = 2.0;
const MAX_SPAN = 3;
const MAX_CANDIDATES = 500;
const ABOVE_WEIGHT = 0.8;
const FALLBACK_MIN_LABEL = 0.5;
const FALLBACK_RULES = new Set(['rounded', 'sign', 'percent']);
const PERCENT_MAX_FRACTION = 10;
// pdf.js gives a run's baseline + font size, not glyph boxes: a glyph box spans ~0.8 size above to ~0.2 below the baseline.
const ASCENT = 0.8;
const DESCENT = 0.2;
const EXTRACTION_PATH = 'text-locate/pdfjs';
const OCR_EXTRACTION_PATH = 'ocr-locate/tesseract';
const INDEX_TAG = 'pdf-provenance-index/2';

function toCents(n) {
  const sign = n < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(n) * 100 + 1e-7);
}

function stripSign(s, neg) {
  if (s && MINUS.includes(s[0])) return [s.slice(1).trim(), !neg];
  return [s, neg];
}

/** Statement-style number -> its signed amount (a JS number), or null. The full precision is kept (0.125 stays 0.125). */
function parseAmount(raw) {
  if (raw === null || raw === undefined || typeof raw === 'boolean') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;
  let s = raw.trim();
  if (!s) return null;
  let neg = false;
  s = s.replace(FOOTNOTE_RE, '').trim();
  if (s.startsWith('(') && s.endsWith(')')) { neg = true; s = s.slice(1, -1).trim(); }
  [s, neg] = stripSign(s, neg);
  for (const code of CURRENCY_CODES) { if (s.toUpperCase().startsWith(code)) s = s.slice(code.length).trim(); }
  s = s.replace(/^[$\u20ac\u00a3]+/, '').trim();
  [s, neg] = stripSign(s, neg);
  if (s.length > 1 && MINUS.includes(s[s.length - 1])) { s = s.slice(0, -1).trim(); neg = !neg; }
  if (s.endsWith('%')) s = s.slice(0, -1).trim();
  if (!NUM_RE.test(s)) return null;
  const amount = parseFloat(s.replace(/,/g, ''));
  return neg ? -amount : amount;
}

/** Statement-style number -> integer cents, or null: 7,530,124.91 / (97,815.31) / \u221297,815.31 / 4,500.00- / $1,234.50 / 12.5% / 12,345.67* */
function parseNumber(raw) {
  const a = parseAmount(raw);
  return a === null ? null : toCents(a);
}

/** How many decimals a printed number shows (7,467,406 -> 0; 250,000.0 -> 1). */
function printedDecimals(raw) {
  const m = /\.(\d+)/.exec(raw || '');
  return m ? m[1].length : 0;
}

/** an amount rounded half up to `decimals` places (0 or 1), returned in cents (7467405.56, 0 -> 746740600). */
function roundedCents(amount, decimals) {
  const f = decimals === 0 ? 1 : 10;
  const sign = amount < 0 ? -1 : 1;
  return toCents(sign * Math.floor(Math.abs(amount) * f + 0.5 + 1e-9) / f);
}

const MONTHS = {};
[['jan', 'january'], ['feb', 'february'], ['mar', 'march'], ['apr', 'april'], ['may'], ['jun', 'june'], ['jul', 'july'],
  ['aug', 'august'], ['sep', 'sept', 'september'], ['oct', 'october'], ['nov', 'november'], ['dec', 'december']]
  .forEach((names, i) => names.forEach((n) => { MONTHS[n] = i + 1; }));
const ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const US_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;
const DMY_TEXT_RE = /^(\d{1,2})[ -]([a-z]+)\.?[ ,-]+(\d{4})$/;
const MDY_TEXT_RE = /^([a-z]+)\.?[ ]+(\d{1,2}),?[ ]+(\d{4})$/;

/** A calendar date -> 'YYYY-MM-DD', or null: 2026-06-30 / 6/30/2026 / 06/30/26 (US month first) / June 30, 2026 / 30 June 2026 / 15-Aug-2026 */
function parseDate(raw) {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase().split(/\s+/).join(' ');
  if (!s || s.length > 24) return null;
  let y = null; let m = null; let d = null; let hit;
  if ((hit = ISO_RE.exec(s))) { y = +hit[1]; m = +hit[2]; d = +hit[3]; }
  else if ((hit = US_RE.exec(s))) { m = +hit[1]; d = +hit[2]; y = +hit[3]; if (y < 100) y += 2000; }
  else if ((hit = DMY_TEXT_RE.exec(s)) && MONTHS[hit[2]]) { d = +hit[1]; m = MONTHS[hit[2]]; y = +hit[3]; }
  else if ((hit = MDY_TEXT_RE.exec(s)) && MONTHS[hit[1]]) { m = MONTHS[hit[1]]; d = +hit[2]; y = +hit[3]; }
  if (y === null) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return String(y).padStart(4, '0') + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

function textTokens(text) {
  if (typeof text !== 'string') return [];
  return text.toLowerCase().split(/\s+/).map((t) => t.replace(EDGE_RE, '')).filter(Boolean);
}

function mul(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1], m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3], m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4], m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

/**
 * pdf.js textContent.items + a viewport (scale 1 recommended) -> words with boxes in DISPLAYED page points, top-left origin.
 * Direction-aware: a run that reads vertically on screen (rotated text, or a /Rotate page with page-upright text) gets a tall
 * box, not a wide one.
 */
function wordsFromItems(items, viewport) {
  const vt = viewport.transform;
  const scale = viewport.scale || 1;
  const words = [];
  for (const it of items || []) {
    const str = it && it.str;
    if (!str || !str.trim()) continue;
    const m = mul(vt, it.transform);
    const ul = Math.hypot(m[0], m[1]) || 1;
    const vl = Math.hypot(m[2], m[3]) || 1;
    const dx = m[0] / ul; const dy = m[1] / ul; // run direction on screen (y down)
    const ux = m[2] / vl; const uy = m[3] / vl; // glyph 'up' on screen
    const size = vl / scale;
    const ox = m[4] / scale; const oy = m[5] / scale;
    const charW = str.length ? (it.width || 0) / str.length : 0; // pdf.js width = the run's advance, in page units
    const re = /\S+/g;
    let hit;
    while ((hit = re.exec(str)) !== null) {
      const a = hit.index * charW;
      const b = a + hit[0].length * charW;
      const xs = []; const ys = [];
      for (const t of [a, b]) {
        const px = ox + dx * t; const py = oy + dy * t;
        xs.push(px + ux * size * ASCENT, px - ux * size * DESCENT);
        ys.push(py + uy * size * ASCENT, py - uy * size * DESCENT);
      }
      words.push({ text: hit[0], x0: Math.min(...xs), top: Math.min(...ys), x1: Math.max(...xs), bottom: Math.max(...ys) });
    }
  }
  return words;
}

function groupLines(words) {
  const sorted = [...words].sort((a, b) => (a.top - b.top) || (a.x0 - b.x0));
  const lines = [];
  for (const w of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last[0].top - w.top) <= LINE_TOL) last.push(w); else lines.push([w]);
  }
  return lines.map((ws) => {
    ws.sort((a, b) => a.x0 - b.x0);
    const tokens = [];
    ws.forEach((w, i) => textTokens(w.text).forEach((t) => tokens.push([t, i])));
    return { words: ws, tokens };
  });
}

function union(ws) {
  return [Math.min(...ws.map((w) => w.x0)), Math.min(...ws.map((w) => w.top)), Math.max(...ws.map((w) => w.x1)), Math.max(...ws.map((w) => w.bottom))];
}

function push(map, key, val) { if (!map.has(key)) map.set(key, []); map.get(key).push(val); }

/** A line handed over already grouped (the OCR step groups lines in the upright image): its words in reading order. */
function lineOf(ws) {
  const tokens = [];
  ws.forEach((w, i) => textTokens(w.text).forEach((t) => tokens.push([t, i])));
  return { words: ws, tokens };
}

/**
 * Build the number + date index for a document's pages once; pass the result to locate() for every click.
 * A page is { number, width, height, words } (from wordsFromItems) or { number, width, height, lines, source: 'ocr' } -- the
 * shape `python -m pdf_provenance words --ocr` writes for scanned pages, lines already grouped.
 */
function buildIndex(pages) {
  const doc = {
    tag: INDEX_TAG,
    pages: pages.map((p) => ({
      number: p.number, width: p.width, height: p.height, source: p.source || 'text',
      lines: Array.isArray(p.lines) ? p.lines.map(lineOf) : groupLines(p.words || []),
    })),
    index: new Map(),
    dates: new Map(),
  };
  doc.textLayer = doc.pages.some((p) => p.lines.length > 0);
  for (const page of doc.pages) {
    page.lines.forEach((line, li) => {
      const ws = line.words;
      for (let i = 0; i < ws.length; i++) {
        for (let k = 1; k <= MAX_SPAN && i + k <= ws.length; k++) {
          const span = ws.slice(i, i + k);
          const digitWords = span.filter((w) => /\d/.test(w.text));
          if (k > 1 && digitWords.length !== 1) continue;
          const joined = span.map((w) => w.text).join('');
          const val = parseNumber(joined);
          if (val === null) continue;
          if (k > 1) {
            const alone = parseNumber(digitWords[0].text);
            if (alone !== null && alone === val) continue;
          }
          // a '%' printed as its own word ('8.0 %') still marks the number as a percent
          const pct = joined.includes('%') || (k === 1 && i + 1 < ws.length && ws[i + 1].text.trim() === '%');
          push(doc.index, val, [page.number, li, i, i + k, printedDecimals(digitWords[0].text), pct]);
        }
        for (let k = 1; k <= MAX_SPAN && i + k <= ws.length; k++) {
          const d = parseDate(ws.slice(i, i + k).map((w) => w.text).join(' '));
          if (d !== null) { push(doc.dates, d, [page.number, li, i, i + k]); break; }
        }
      }
    });
  }
  return doc;
}

function pageOf(doc, n) { return doc.pages[n - 1]; }

function cand(doc, p, li, s, e) {
  return { page: p, line: li, start: s, end: e, bbox: union(pageOf(doc, p).lines[li].words.slice(s, e)), score: 0, label_bbox: null };
}

function numberCandidates(doc, cents, decimals, percent) {
  return (doc.index.get(cents) || [])
    .filter(([, , , , dec, pct]) => (decimals === undefined || dec === decimals) && (percent === undefined || pct === percent))
    .map(([p, li, s, e]) => cand(doc, p, li, s, e));
}

function dateCandidates(doc, iso) {
  return (doc.dates.get(iso) || []).map(([p, li, s, e]) => cand(doc, p, li, s, e));
}

function textCandidates(doc, value) {
  const want = textTokens(value);
  const out = [];
  if (!want.length) return out;
  for (const page of doc.pages) {
    page.lines.forEach((line, li) => {
      const toks = line.tokens;
      for (let j = 0; j + want.length <= toks.length; j++) {
        let ok = true;
        for (let q = 0; q < want.length; q++) { if (toks[j + q][0] !== want[q]) { ok = false; break; } }
        if (!ok) continue;
        const s = toks[j][1];
        const e = toks[j + want.length - 1][1] + 1;
        out.push({ page: page.number, line: li, start: s, end: e, bbox: union(line.words.slice(s, e)), score: 0, label_bbox: null });
      }
    });
  }
  return out;
}

function* tiers(doc, value) {
  const amount = parseAmount(value);
  if (amount !== null) {
    const num = toCents(amount);
    yield ['exact', numberCandidates(doc, num)];
    for (const d of [0, 1]) yield ['rounded', numberCandidates(doc, roundedCents(amount, d), d)];
    if (num !== 0) yield ['sign', numberCandidates(doc, -num)];
    if (Math.abs(amount) < PERCENT_MAX_FRACTION) yield ['percent', numberCandidates(doc, toCents(amount * 100), undefined, true)];
    return;
  }
  const iso = typeof value === 'string' ? parseDate(value) : null;
  if (iso !== null) { yield ['date', dateCandidates(doc, iso)]; return; }
  yield ['text', textCandidates(doc, String(value))];
}

function labelScore(doc, c, keyTokens) {
  if (!keyTokens.length) return;
  const want = new Set(keyTokens);
  const page = pageOf(doc, c.page);
  const line = page.lines[c.line];
  const left = line.tokens.filter(([, i]) => i < c.start);
  const hit = new Set(left.filter(([t]) => want.has(t)).map(([t]) => t));
  let best = hit.size / want.size;
  let bestWords = left.filter(([t]) => want.has(t)).map(([, i]) => line.words[i]);
  if (c.line > 0) {
    const above = page.lines[c.line - 1];
    const hitA = new Set(above.tokens.filter(([t]) => want.has(t)).map(([t]) => t));
    const scoreA = ABOVE_WEIGHT * hitA.size / want.size;
    if (scoreA > best) { best = scoreA; bestWords = above.tokens.filter(([t]) => want.has(t)).map(([, i]) => above.words[i]); }
  }
  c.score = best;
  c.label_bbox = bestWords.length ? union(bestWords) : null;
}

const r2 = (v) => Math.round(v * 100) / 100;
const r6 = (v) => Math.round(v * 1e6) / 1e6;

function printedText(doc, c) { return pageOf(doc, c.page).lines[c.line].words.slice(c.start, c.end).map((w) => w.text).join(' '); }

function record(doc, docName, c, confidence, match, rule) {
  const page = pageOf(doc, c.page);
  const bbox = c.bbox.map(r2);
  const w = page.width;
  const h = page.height;
  return {
    doc: docName, page: c.page, bbox,
    bbox_norm: [r6(bbox[0] / w), r6(bbox[1] / h), r6(bbox[2] / w), r6(bbox[3] / h)],
    page_size: [r2(w), r2(h)],
    label_bbox: c.label_bbox ? c.label_bbox.map(r2) : null,
    confidence: Math.round(confidence * 1000) / 1000,
    extraction_path: page.source === 'ocr' ? OCR_EXTRACTION_PATH : EXTRACTION_PATH, match, rule, printed_text: printedText(doc, c),
  };
}

function empty(doc, docName, match) {
  return { doc: docName, page: null, bbox: null, bbox_norm: null, page_size: null, label_bbox: null, confidence: 0, extraction_path: EXTRACTION_PATH, match, rule: null, printed_text: null, text_layer: doc.textLayer };
}

function ambiguous(doc, docName, cands, rule, groupPage) {
  const rec = empty(doc, docName, 'ambiguous');
  rec.rule = rule;
  const gp = Number.isInteger(groupPage) ? groupPage : null;
  const order = (a, b) => (Number(a.page !== gp) - Number(b.page !== gp)) || (a.page - b.page) || (a.bbox[1] - b.bbox[1]) || (a.bbox[0] - b.bbox[0]);
  const shown = [...cands].sort(order).slice(0, MAX_CANDIDATES);
  rec.candidates = shown.map((c) => { const x = record(doc, docName, c, 0, 'candidate', rule); return { page: x.page, bbox: x.bbox, bbox_norm: x.bbox_norm, page_size: x.page_size }; });
  rec.candidate_count = cands.length;
  rec.candidate_count_capped_at = MAX_CANDIDATES;
  rec.pages = Array.from(new Set(cands.map((c) => c.page))).sort((a, b) => a - b);
  rec.group_page = gp;
  rec.group_page_count = gp === null ? 0 : cands.filter((c) => c.page === gp).length;
  return rec;
}

function top(cands) {
  const best = Math.max(...cands.map((c) => c.score));
  return cands.filter((c) => Math.abs(c.score - best) < 1e-9);
}

const clone = (x) => (typeof structuredClone === 'function' ? structuredClone(x) : JSON.parse(JSON.stringify(x)));

/** Return a copy of items, each with an added `provenance` field. First argument: pages (see top) or a buildIndex() result. */
function locate(pagesOrIndex, items, docName = 'document.pdf') {
  const doc = pagesOrIndex && pagesOrIndex.tag === INDEX_TAG ? pagesOrIndex : buildIndex(pagesOrIndex || []);
  const out = items.map((it) => clone(it));
  const pending = [];
  const groupPages = new Map();
  out.forEach((item, idx) => {
    const value = item.value;
    if (value === null || value === undefined) { item.provenance = null; return; }
    const keyTokens = textTokens(item.key);
    let rule = null; let cands = [];
    for (const [r, csIn] of tiers(doc, value)) {
      if (!csIn.length) continue;
      csIn.forEach((c) => labelScore(doc, c, keyTokens));
      const cs = FALLBACK_RULES.has(r) ? csIn.filter((c) => c.score >= FALLBACK_MIN_LABEL) : csIn;
      if (!cs.length) continue;
      rule = r; cands = cs; break;
    }
    if (!cands.length) { item.provenance = empty(doc, docName, 'not_found'); return; }
    const fallback = FALLBACK_RULES.has(rule);
    const total = cands.length;
    let reason = 'label';
    if (Number.isInteger(item.page_hint)) {
      const hinted = cands.filter((c) => c.page === item.page_hint);
      if (hinted.length) { if (hinted.length < cands.length) reason = 'hint'; cands = hinted; }
    }
    const t = top(cands);
    if (t.length === 1) {
      const c = t[0];
      let match; let conf;
      if (fallback) { match = rule; conf = 0.3 + 0.4 * c.score; }
      else if (total === 1) { match = 'unique'; conf = 0.6 + 0.4 * c.score; }
      else { match = cands.length === 1 ? reason : 'label'; conf = 0.5 + 0.4 * c.score; }
      item.provenance = record(doc, docName, c, conf, match, rule);
      if (item.group !== undefined && item.group !== null) {
        if (!groupPages.has(item.group)) groupPages.set(item.group, new Map());
        const m = groupPages.get(item.group);
        m.set(c.page, (m.get(c.page) || 0) + 1);
      }
    } else {
      pending.push([idx, t, rule]);
    }
  });
  for (const [idx, t, rule] of pending) {
    const item = out[idx];
    let chosen = null;
    let groupPage = null;
    const counts = item.group !== undefined && item.group !== null ? groupPages.get(item.group) : null;
    if (counts && counts.size) {
      const peak = Math.max(...counts.values());
      const peakPages = new Set([...counts.entries()].filter(([, n]) => n === peak).map(([p]) => p));
      const onPeak = t.filter((c) => peakPages.has(c.page));
      const onGroup = t.filter((c) => counts.has(c.page)); // the group spans several pages (one investor, two statements)
      if (onPeak.length === 1) chosen = onPeak[0];
      else if (onGroup.length === 1) chosen = onGroup[0];
      else {
        // printed more than once on the group's page (e.g. a Current Period and a YTD column): no single spot, but the page is
        // known when every such place sits on one page -- a tie across pages stays a plain ambiguous
        const near = onPeak.length ? onPeak : onGroup;
        const nearPages = new Set(near.map((c) => c.page));
        if (nearPages.size === 1) groupPage = [...nearPages][0];
      }
    }
    if (chosen) {
      const fallback = FALLBACK_RULES.has(rule);
      item.provenance = record(doc, docName, chosen, (fallback ? 0.3 : 0.5) + 0.3 * chosen.score, fallback ? rule : 'group', rule);
    } else {
      item.provenance = ambiguous(doc, docName, t, rule, groupPage);
    }
  }
  return out;
}

const api = { parseNumber, parseDate, textTokens, wordsFromItems, buildIndex, locate, EXTRACTION_PATH };
if (typeof module !== 'undefined' && module.exports) module.exports = api;
else if (root) root.valueLocator = api;
})(typeof window !== 'undefined' ? window : undefined);
